const DEFAULT_SAVE_DELAY = 1000

const getService = (context, name) => {
    const service = context.services && context.services[name]

    if (typeof service !== 'function') {
        throw new Error(`Review and Submit requires services.${name}()`)
    }

    return service
}

const createReviewAndSubmitController = ({
    context,
    setDirty = () => {},
    setSaving = () => {},
    publishEffect = () => Promise.resolve(),
    reportError = () => {},
    saveDelay = DEFAULT_SAVE_DELAY,
}) => {
    let validation = { isValid: false, errors: [] }
    let saveTimer = null
    let pendingSave = null
    let revision = 0
    let savedRevision = 0
    let disposed = false

    const scheduleSave = () => {
        clearTimeout(saveTimer)
        saveTimer = setTimeout(() => {
            saveTimer = null
            // eslint-disable-next-line no-use-before-define
            saveDetails().catch(() => undefined)
        }, saveDelay)
    }

    const saveDetails = () => {
        if (pendingSave || savedRevision >= revision) {
            return pendingSave || Promise.resolve()
        }

        const savingRevision = revision
        setSaving(true)
        const operation = Promise.resolve()
            .then(() => getService(context, 'saveReceivedRequest')())
            .then(() => publishEffect({
                type: 'request.other-information.changed',
                phase: 'committed',
                scopes: ['request.other-information'],
            }))
            .then(() => {
                savedRevision = Math.max(savedRevision, savingRevision)
                setDirty(savedRevision < revision)
                reportError(null)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                if (pendingSave === operation) {
                    pendingSave = null
                    setSaving(false)
                    if (savedRevision < revision) {
                        scheduleSave()
                    }
                }
            })

        pendingSave = operation
        return operation
    }

    const flushDetails = async () => {
        clearTimeout(saveTimer)
        saveTimer = null
        while (pendingSave || savedRevision < revision) {
            if (pendingSave) {
                // eslint-disable-next-line no-await-in-loop
                await pendingSave
            } else {
                // eslint-disable-next-line no-await-in-loop
                await saveDetails()
            }
        }
    }

    const invoke = (callback, value) => {
        if (typeof callback === 'function') {
            callback(value)
        }
    }

    return Object.freeze({
        setValidation(nextValidation) {
            validation = nextValidation
        },
        validate() {
            return Promise.resolve(validation)
        },
        async handleAction(action) {
            if (disposed) {
                return
            }

            const payload = action.payload || {}
            switch (action.type) {
            case 'submission-options.change':
                Object.assign(context.replySubmission, payload)
                return
            case 'other-information.change':
                getService(context, 'changeOtherInformation')(payload.otherInformation)
                revision += 1
                setDirty(true)
                Promise.resolve(publishEffect({
                    type: 'request.other-information.changed',
                    phase: 'draft',
                    scopes: ['request.other-information'],
                })).catch(reportError)
                scheduleSave()
                return
            case 'approval.code.request':
                try {
                    await getService(context, 'requestDeclarationApprovalCode')({
                        name: payload.name,
                        email: payload.email,
                    })
                    invoke(payload.completionCallback)
                } catch (error) {
                    invoke(payload.failureCallback, error)
                }
                return
            case 'approval.code.verify':
                try {
                    await getService(context, 'verifyDeclarationApprovalCode')(payload.code)
                    invoke(payload.completionCallback)
                } catch (error) {
                    invoke(payload.failureCallback, error)
                }
                return
            case 'terminal.execute':
                if (!validation.isValid) {
                    throw new Error('Review and Submit is incomplete')
                }
                await flushDetails()
                try {
                    const result = await getService(context, 'sendReceivedRequestReply')()
                    reportError(null)
                    return result
                } catch (error) {
                    reportError(error)
                    throw error
                }
            default:
                throw new Error(`Unsupported Review and Submit action "${action.type}"`)
            }
        },
        flush: flushDetails,
        dispose() {
            disposed = true
            clearTimeout(saveTimer)
            saveTimer = null
        },
    })
}

export { DEFAULT_SAVE_DELAY, createReviewAndSubmitController }
export default createReviewAndSubmitController
